import { useLocation, useParams } from 'react-router-dom';

import type { Routes } from '../../router/types';

// by module path, not through the `router/components` barrel: that barrel
// also loads `Breadcrumbs`, and with it the whole `src/context` tree.
import Navigate from '../../router/components/Navigate';

export type BusinessFinancePath =
  | 'marketplace-payouts'
  | 'performance'
  | 'platform-analytics'
  | 'profits-expenses';

export interface LegacyBusinessFinanceRedirectProps {
  target: BusinessFinancePath;
  /** Old `?tab=` value → where it lives now. Unlisted values are dropped. */
  tabs?: Record<string, { target?: BusinessFinancePath; tab?: string }>;
  /** Tab to open when the old URL carried none (or one not in `tabs`). */
  defaultTab?: string;
}

/**
 * An old Business & Finance address (`/stats`, `/booking-stats`,
 * `/finance/stats`, `/payouts`) — saved links and bookmarks land on the page
 * that now holds the same content instead of on nothing.
 */
export const LegacyBusinessFinanceRedirect = ({
  defaultTab,
  tabs = {},
  target,
}: LegacyBusinessFinanceRedirectProps) => {
  const { organizationId = '' } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const mapped = tabs[params.get('tab') ?? ''];
  const path = mapped?.target ?? target;
  const tab = mapped?.tab ?? defaultTab;

  params.delete('tab');
  if (tab) {
    params.set('tab', tab);
  }
  const query = params.toString();

  return (
    <Navigate
      to={
        `/organizations/${organizationId}/${path}${query ? `?${query}` : ''}` as Routes
      }
      replace
    />
  );
};
